import './CompanyProfileView.scss';

import React, { PureComponent } from 'react';
import MetaTags from 'react-meta-tags';
import { connect } from 'react-redux';
import { List, Map } from 'immutable';
import PropTypes from 'prop-types';

import * as actions from 'actions/hiring';

import * as entitiesSelectors from 'selectors/entities';
import * as hiringSelectors from 'selectors/hiring';
import * as routeSelectors from 'selectors/route';
import * as sessionSelectors from 'selectors/session';

import Loader from 'components/Loader';

import cxHelpers from 'util/className';
import * as formActions from 'util/forms';
import { toI18n } from 'util/i18n';
import { logHiringEvent } from 'util/tracking';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';
import { withUxRoot, withUxTracking } from 'util/uxEvents';

import PoweredByHomebaseBadge from '../../PoweredByHomebaseBadge';
import TextToApplyWidget from '../../promote/TextToApplyWidget';
import CompanyProfileHeader from '../CompanyProfileHeader';
import CompanyProfileImageBanner from '../CompanyProfileImageBanner';
import CompanyProfileOverview from '../CompanyProfileOverview';
import LocationProfile from '../LocationProfile';
import LocationProfileForm from '../LocationProfileForm';

@connect(
  (state, props) => ({
    isLoading: hiringSelectors.getHiringCompanyProfileIsLoading(state, props),
    companyProfileId: routeSelectors.getRouteParam(state, props, 'id'),
    companyProfile: hiringSelectors.getCompanyProfileFromRoute(
      state,
      props,
      'id'
    ),
    locationProfiles: hiringSelectors.getLocationProfilesFromRoute(
      state,
      props,
      'id'
    ),
    jobRequests: entitiesSelectors.getJobRequests(state, props),
    canEdit: hiringSelectors.getCanEditCompanyProfile(state, props),
    states: sessionSelectors.getStates(state, props),
    isSubmitting: hiringSelectors.getHiringCompanyProfileFormIsSubmitting(
      state,
      props
    ),
    isPristine: hiringSelectors.companyProfileFormSelectors.getIsPristine(
      state,
      props
    ),
    isEditing: hiringSelectors.getHiringCompanyProfileIsEditing(state, props),
  }),
  {
    fetchCompanyProfile: actions.fetchCompanyProfile,
    submitForm: actions.submitCompanyProfileForm,
    toggleEditing: actions.toggleEditingCompanyProfileForm,
    updateFormField: formActions.updateFormField('COMPANY_PROFILE_FORM'),
  }
)
@withUxRoot({
  productArea: PRODUCT_AREAS.HIRING,
  eventCategory: EVENT_CATEGORIES.HIRING_CAREER_PAGE,
})
@withUxTracking()
@cxHelpers('CompanyProfileView')
export default class CompanyProfileView extends PureComponent {
  static propTypes = {
    isLoading: PropTypes.bool.isRequired,
    companyProfileId: PropTypes.string.isRequired,
    companyProfile: PropTypes.instanceOf(Map),
    locationProfiles: PropTypes.instanceOf(Map),
    jobRequests: PropTypes.instanceOf(Map),
    canEdit: PropTypes.bool,
    fetchCompanyProfile: PropTypes.func.isRequired,
    submitForm: PropTypes.func.isRequired,
    states: PropTypes.instanceOf(List).isRequired,
    isSubmitting: PropTypes.bool,
    isPristine: PropTypes.bool,
  };

  // TODO: https://joinhomebase.atlassian.net/browse/HIRING-441
  UNSAFE_componentWillMount() {
    this.props.fetchCompanyProfile(this.props.companyProfileId);
  }

  componentDidMount() {
    logHiringEvent({
      product_location: 'careers_page',
      action: 'view_page',
    });

    this.props.trackUx(EVENT_ACTIONS.PAGE_VIEWED, TRACK_ACTION_TYPES.VIEW);
  }

  submitForm = () => this.props.submitForm(this.props.companyProfileId);

  toggleEditing = () => {
    if (!this.props.isEditing) {
      logHiringEvent({
        product_location: 'careers_page',
        action: 'edit',
      });
    }

    this.props.toggleEditing(
      !this.props.isEditing,
      this.props.companyProfile,
      this.props.locationProfiles
    );
  };

  renderLocationProfiles = () =>
    this.props.locationProfiles.valueSeq().map(locationProfile => {
      const jobRequests = this.props.jobRequests.filter(jobRequest =>
        locationProfile.get('job_requests').includes(jobRequest.get('id'))
      );

      const sharedProps = {
        key: locationProfile.get('id'),
        locationProfile,
        jobRequests,
      };

      return this.props.isEditing ? (
        <LocationProfileForm states={this.props.states} {...sharedProps} />
      ) : (
        <LocationProfile {...sharedProps} />
      );
    });

  render() {
    const {
      isLoading,
      canEdit,
      isSubmitting,
      isPristine,
      isEditing,
      companyProfile,
      updateFormField,
    } = this.props;

    const metaTitle = toI18n('hiring.company_profile.meta.title', {
      props: {
        store: companyProfile?.get('title'),
      },
    }).replace(/<[^>]+>/g, '');

    const arr = [];
    this.props.jobRequests.forEach(jobRequest =>
      arr.push(jobRequest.get('role_name'))
    );

    const metaDescription = toI18n('hiring.company_profile.meta.description', {
      props: {
        store: companyProfile?.get('title'),
        job_positions: arr.join(', '),
      },
    }).replace(/<[^>]+>/g, '');

    return (
      <div className={this.cx(null, 'bootstrap')}>
        <MetaTags>
          <link rel="canonical" href={window.location.href} key="linkMeta" />
          <title>{metaTitle}</title>
          <meta property="og:title" content={metaTitle} />
          <meta property="og:description" content={metaDescription} />
          <meta name="description" content={metaDescription} />
        </MetaTags>
        <Loader isLoading={isLoading} className={this.cxEl('loader')} centered>
          <div>
            <div className={this.cxEl('content', { 'with-header': canEdit })}>
              {canEdit ? (
                <CompanyProfileHeader
                  isEditing={isEditing}
                  isSubmitting={isSubmitting}
                  toggleEditing={this.toggleEditing}
                  submitDisabled={isPristine}
                  submitForm={this.submitForm}
                  companyProfile={companyProfile}
                />
              ) : null}

              {companyProfile ? (
                <div>
                  <CompanyProfileImageBanner
                    isEditing={isEditing}
                    coverSrc={companyProfile.get('cover')}
                    logoSrc={companyProfile.get('logo')}
                    updateFormField={updateFormField}
                  />
                  <CompanyProfileOverview
                    companyProfile={companyProfile}
                    isEditing={isEditing}
                  />
                </div>
              ) : null}

              {this.renderLocationProfiles()}

              <PoweredByHomebaseBadge className={this.cxEl('homebase-badge')} />
            </div>

            {canEdit && companyProfile ? (
              <div className={this.cxEl('footer')}>
                <TextToApplyWidget
                  className={this.cxEl('text-to-apply')}
                  textToApplyNumber={companyProfile.get('text_to_apply_number')}
                  textToApplyCode={companyProfile.get('text_to_apply_code')}
                />
              </div>
            ) : null}
          </div>
        </Loader>
      </div>
    );
  }
}
